import { getDialCodeForCountry, getDialCodePrefix, applyDialCodeToPhone } from '../../utils/phoneFormatter';

/**
 * DialCodePhoneInput — a "type-to-reveal" phone field.
 *
 * The field is genuinely empty on open and on focus: the country dial code
 * (e.g. "+256") is never shown as text or placeholder. The moment the user
 * types the first digit, the dial code chip animates in beside the number
 * (fade + slide + pop), acting as a protected prefix that cannot be deleted.
 * Clearing the field makes the chip fade away again.
 *
 * The parent state always holds the full E.164 string (e.g. "+256-755-569-649")
 * exactly as before, so validation/submission logic is untouched.
 *
 * Props:
 *   - name, country: passed through for form handling / dial code lookup
 *   - value: full formatted phone (e.g. "+256-755-569-649") or ''
 *   - onChange: called with the new full value (or '' when cleared)
 */
const DialCodePhoneInput = ({ name, id, value = '', country, onChange, placeholder = 'e.g. 755 569 649', wrapperClassName = '', inputClassName = '', required = false, autoComplete = 'off' }) => {
  // Split the stored value into dial code + local number for the split display
  const match = value ? value.match(/^\+(\d{1,3})-?(.*)$/) : null;
  const valueCode = match ? '+' + match[1] : null;
  const local = match ? match[2] : value;
  const hasNumber = local.replace(/\D/g, '').length > 0;

  const countryCode = getDialCodeForCountry(country);
  const code = valueCode || (countryCode ? '+' + countryCode : '');

  const handleChange = (e) => {
    const raw = e.target.value;
    if (!raw.replace(/\D/g, '')) {
      onChange('');
      return;
    }
    const prefix = valueCode || getDialCodePrefix(country) || '+';
    onChange(applyDialCodeToPhone(prefix + raw, country));
  };

  return (
    <div className={`relative ${wrapperClassName}`}>
      <span
        aria-hidden="true"
        title="Dial code is added automatically as soon as you start typing"
        className={`pointer-events-none absolute left-2.5 top-1/2 z-10 flex -translate-y-1/2 select-none items-center gap-1 rounded-md bg-teal-500/10 px-1.5 py-0.5 text-xs font-semibold tracking-wide text-teal-700 transition-all duration-300 ease-out dark:text-teal-300 ${
          hasNumber ? 'translate-x-0 scale-100 opacity-100' : '-translate-x-2 scale-75 opacity-0'
        }`}
      >
        {code}
        <span className="h-3 w-px bg-teal-600/40" />
      </span>
      <input
        type="tel"
        id={id}
        name={name}
        inputMode="tel"
        autoComplete={autoComplete}
        required={required}
        value={local}
        onChange={handleChange}
        placeholder={placeholder}
        className={`input-rx text-xs ${inputClassName}`}
        style={{ paddingLeft: code ? `${0.5 * code.length + 1.45}rem` : '0.75rem' }}
      />
    </div>
  );
};

export default DialCodePhoneInput;
